import _ from "lodash";
import React, { CSSProperties, FC, useCallback, useState } from "react";
import {
  ActivityIndicator,
  FlatList,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  TouchableOpacityProps,
  View,
  ViewProps,
} from "react-native";
import { useSelector } from "react-redux";

import {
  addPromoCode,
  addQuantity,
  applyRewardsCredit,
  changeCartAddress,
  editPONumber,
  openCheckoutSingle,
  removeProduct,
  setQuantity,
  subtractQuantity,
} from "../redux/user/actions";
import { sortBySku } from "../util/cart";
import { formatMoney, formatPercentage, formatPhoneNumber, formatShortAddress } from "../util/gFormatting";
import { tCartProduct, tConnectionStatus, tDetailedCart, tVariant } from "tmt_api/src/types/types";
import NumberInput, { tNumberInputProps } from "./NumberInput";
import * as connectionDb from "src/db/connection.db";
import { fonts } from "src/fonts";
import { SLImage } from "./ReactiveImage";
import { useNavigation } from "@react-navigation/native";
import { useAppDispatch } from "src/redux";
import { kRoute } from "src/kRoute";
import { kColors } from "src/kColors";
import { kStyleConst } from "src/kStyle";
import { kFonts } from "src/kFonts";
import { AddProductModal, tAddProductModal, tProductModal } from "./Modals/AddProductModal";
import { useModal } from "src/providers/OverlayProvider";
import { useConfirmModal, useRetailerAddressesModal } from "src/hooks/modals";
import { isApiError } from "src/util/apiNew";
import { PdStack, PdText } from "./layout";
import { PdButton, PdButtonLabel } from "./forms/button";
import { useIsMobileAndTablet } from "src/hooks/ui";
import { PdIconButton } from "./forms/icon-button";
import { PdClose } from "./Icons";
import { TesterBadge } from "./ui";
import { PdCheckbox, PdCheckboxLabel } from "./forms/checkbox";

type tBrandCartItem = {
  item: tCartProduct;
};

const getVariant = (cartProduct: tCartProduct): tVariant | undefined => {
  return cartProduct.variants.find((variant) => variant.sku === cartProduct.actualSKU);
};

const getTesterVariant = (cartProduct: tCartProduct): tVariant | undefined => {
  if (!cartProduct.isTester) return undefined;
  return cartProduct.variants.find((testerVariant) => cartProduct.actualSKU === testerVariant.tester?.sku);
};

const ProductImage: React.FC<TouchableOpacityProps & { uri?: string }> = ({ uri, ...rest }) => {
  return (
    <TouchableOpacity {...rest}>
      <SLImage mobileStyle={styles.placeholderPortrait} webStyle={webStyles.placeholderPortrait} source={{ uri }} />
    </TouchableOpacity>
  );
};

const ProductVariantDescriptions: React.FC<{ options?: { name: string; label: string }[] }> = ({ options }) => {
  if (!options) return null;
  return (
    <>
      {options.map((option, index) => (
        <Text key={index} style={styles.brandCartItemName}>
          {option.name}: {option.label}
        </Text>
      ))}
    </>
  );
};

const ControlSubtractButton: React.FC<TouchableOpacityProps> = (props) => {
  return (
    <TouchableOpacity style={[styles.alignCenter, styles.justifyCenter, styles.w20]} {...props}>
      <PdText style={styles.cartQuantityChangeButtonLabel}>-</PdText>
    </TouchableOpacity>
  );
};

const ControlQuantityInput: React.FC<{ isLoading?: boolean } & tNumberInputProps> = ({ isLoading, ...rest }) => {
  if (isLoading) {
    return (
      <View style={styles.w45}>
        <ActivityIndicator color={kColors.brandDarkGreen} size="small" />
      </View>
    );
  }

  return <NumberInput style={styles.w45} textInputStyle={styles.bodyText} {...rest} />;
};

const ControlAddButton: React.FC<TouchableOpacityProps> = (props) => {
  return (
    <TouchableOpacity style={[styles.alignCenter, styles.justifyCenter, styles.w20]} {...props}>
      <PdText style={styles.cartQuantityChangeButtonLabel}>+</PdText>
    </TouchableOpacity>
  );
};

const Controls: React.FC<
  ViewProps & { item: tCartProduct; onSuccess?: () => void; onError?: (error: string) => void }
> = ({ style, item, onSuccess, onError, ...rest }) => {
  const dispatch = useAppDispatch();
  const [sQuantityLoading, _setQuantityLoading] = React.useState<boolean>(false);

  const _addQuantity = async () => {
    try {
      _setQuantityLoading(true);
      await dispatch(addQuantity(item));
      onSuccess?.();
    } catch (error) {
      if (isApiError(error)) {
        onError?.(error.response?.data?.message ?? "An error occurred please try again.");
      }
    } finally {
      _setQuantityLoading(false);
    }
  };

  const _subtractQuantity = async () => {
    try {
      _setQuantityLoading(true);
      await dispatch(subtractQuantity(item));
      onSuccess?.();
    } catch (error) {
      if (isApiError(error)) {
        onError?.(error.response?.data?.message ?? "An error occurred please try again.");
      }
    } finally {
      _setQuantityLoading(false);
    }
  };

  const _changeQuantity = useCallback(
    async (myNumber) => {
      const kHasCaseQtyChanged =
        item.isTester || myNumber >= item.quantity + item.case_minimum || myNumber < item.quantity;
      const myTargetQuantity = item.isTester ? myNumber : myNumber - (myNumber % item.case_minimum);
      const myDiff = myTargetQuantity - item.quantity;
      if (kHasCaseQtyChanged) {
        try {
          _setQuantityLoading(true);
          await dispatch(
            setQuantity({
              productActualSku: item.actualSKU,
              brandId: item.brandUid,
              productId: item.productId,
              case_minimum: item.case_minimum,
              inventory: item.inventory,
              currentQuantity: myDiff,
              isTester: item.isTester,
            }),
          );
        } finally {
          _setQuantityLoading(false);
        }
      }
    },
    [
      dispatch,
      item.actualSKU,
      item.brandUid,
      item.case_minimum,
      item.inventory,
      item.productId,
      item.isTester,
      item.quantity,
    ],
  );

  if (item.isOutOfStock) return null;

  return (
    <View style={[styles.flexRow, styles.alignCenter, style]} {...rest}>
      <ControlSubtractButton onPress={_subtractQuantity} />
      <ControlQuantityInput
        isLoading={sQuantityLoading}
        onInputNumber={_changeQuantity}
        value={item.quantity.toString()}
      />
      <ControlAddButton onPress={_addQuantity} />
    </View>
  );
};

const ApplyRewardsCheckbox: FC<{ item: tDetailedCart }> = ({ item }) => {
  const rCompany = useSelector((state) => state.CompanyReducer);
  const dispatch = useAppDispatch();

  const onPress = async () => {
    if (rCompany.type !== "retailer") return;
    await dispatch(applyRewardsCredit(item.cartId));
  };

  if (rCompany.type !== "retailer") {
    return null;
  }

  const creditUsed = (item.creditUsedAmount ?? 0) + (item.marketplaceCreditUsedAmount ?? 0);

  return (
    <>
      <PdCheckbox value={!!creditUsed} onPress={onPress}>
        <PdCheckboxLabel>Apply Main Tab Rewards</PdCheckboxLabel>
      </PdCheckbox>
      {!!creditUsed && (
        <PdText style={[styles.textGreen, styles.textSm, styles.textRight, styles.mt1]}>{`${formatMoney(
          creditUsed,
        )} in rewards will be refunded to you when this order ships! Please complete checkout as usual.`}</PdText>
      )}
    </>
  );
};

export const BrandCartItem: FC<tBrandCartItem> = ({ item }) => {
  const dispatch = useAppDispatch();
  const isMobileTablet = useIsMobileAndTablet();
  const kNav = useNavigation();
  const addVariantProductModal = useModal<tAddProductModal, void>(AddProductModal);
  const [warningMessage, setWarningMessage] = useState("");

  const handleOnPressProductImage = () => {
    const product: tProductModal = {
      name: item.name,
      brandUid: item.brandUid,
      brandName: item.brandName,
      tester: item.tester,
      images: item.images,
      variants: item.variants,
      availability: item.availability,
      sku: item.sku,
      wholesalePrice: item.wholesale_price,
      msrpPrice: item.msrp_price,
      caseMinimum: item.case_minimum,
      productId: item.productId,
      inventory: item.inventory,
    };
    addVariantProductModal.openAsync({ action: "edit", product });
  };

  const clearWarningMessage = () => {
    setWarningMessage("");
  };

  const _removeProduct = useCallback(
    () => dispatch(removeProduct(item.actualSKU, item.brandUid, item.productId)),
    [dispatch, item.actualSKU, item.brandUid, item.productId],
  );

  const _handleNavToProduct = useCallback(() => {
    kNav.navigate(kRoute.P101, {
      brand: item.brandName,
      productId: item.productId,
    });
  }, [item.brandName, item.productId, kNav]);

  const aVariant = getVariant(item);
  const aTesterVariant = getTesterVariant(item);

  let kImageUrl = item.images?.[0]?.image_url;
  if (aTesterVariant?.imageUrl) {
    kImageUrl = aTesterVariant.imageUrl;
  } else if (aVariant?.imageUrl) {
    kImageUrl = aVariant.imageUrl;
  }

  return (
    <View style={[styles.flexRow, styles.my2]}>
      <ProductImage onPress={handleOnPressProductImage} uri={kImageUrl} />
      <View
        style={[
          isMobileTablet ? styles.flexColumn : [styles.flexRow, styles.alignCenter],
          styles.flexWrap,
          styles.flex1,
          styles.ml4,
        ]}
      >
        <View style={[styles.flex1, styles.mbAuto]}>
          <TouchableOpacity style={isMobileTablet ? styles.mr7 : styles.flex1} onPress={_handleNavToProduct}>
            <View
              style={[
                isMobileTablet ? styles.flexColumn : [styles.flexRow, styles.alignCenter],
                styles.flex1,
                styles.flexWrap,
                styles.mb1,
              ]}
            >
              <PdText numberOfLines={1} style={styles.brandCartItemName}>
                {item.name}
              </PdText>
              {item.isTester && <TesterBadge style={isMobileTablet ? {} : styles.ml2} />}
            </View>
            <ProductVariantDescriptions options={aVariant?.options} />
            {item.isTester && <ProductVariantDescriptions options={aTesterVariant?.options} />}
          </TouchableOpacity>
          {item.isOutOfStock && <Text style={styles.outOfStockLabel}>Sorry, this product is no longer in stock.</Text>}
          {!!warningMessage && <Text style={styles.outOfStockLabel}>{warningMessage}</Text>}
        </View>
        <View
          style={[
            styles.flexRow,
            styles.flexWrap,
            styles.alignCenter,
            styles.justifyBetween,
            isMobileTablet ? styles.wFull : styles.w250,
          ]}
        >
          <Controls item={item} onSuccess={clearWarningMessage} onError={setWarningMessage} />
          <PdText
            style={[
              styles.brandInCartItemInfoTextCheckOut,
              isMobileTablet ? {} : [styles.flex1, styles.ml5, styles.mr5],
            ]}
          >
            {formatMoney(item.totalPrice)}
          </PdText>
        </View>
        <PdIconButton
          size="sm"
          onPress={_removeProduct}
          variant="ghost"
          icon={<PdClose style={styles.removeProductIcon} />}
          style={isMobileTablet ? styles.removeProductButtonAbsolute : {}}
        />
      </View>
    </View>
  );
};

const CheckoutButton: React.FC<{
  connectionStatus?: tConnectionStatus;
  cart: tDetailedCart;
  onRequestToConnect: () => void;
}> = ({ connectionStatus, cart, onRequestToConnect }) => {
  const dispatch = useAppDispatch();
  const confirmModal = useConfirmModal();

  const handleOnPressSingleCheckout = async () => {
    const response = await dispatch(openCheckoutSingle(cart));

    if (response && response.cartsFinished.length > 0 && response.cartsNotFinished.length === 0) {
      confirmModal.openAsync({
        title: "Success!",
        description: "Your order has been submitted. You can view your order and its status in your Order page.",
        confirmButtonLabel: "OKAY",
        hideCancelButton: true,
      });
    }
  };

  const handleOnPressConnectWithBrand = () => {
    onRequestToConnect();
  };

  if (connectionStatus !== "Approved") {
    return (
      <PdButton colorScheme="purple" isDisabled={!!connectionStatus} onPress={handleOnPressConnectWithBrand}>
        <PdButtonLabel>{connectionStatus ? "Connection Status Pending" : "Connect with Brand"}</PdButtonLabel>
      </PdButton>
    );
  }

  if (!cart.overMin) {
    return (
      <PdButton isDisabled>
        <PdButtonLabel>
          {cart.orderDiff ? `${formatMoney(cart.orderDiff)} Under Order Minimum` : "Under Order Minimum"}
        </PdButtonLabel>
      </PdButton>
    );
  }

  return (
    <PdButton onPress={handleOnPressSingleCheckout}>
      <PdButtonLabel>Checkout this Brand</PdButtonLabel>
    </PdButton>
  );
};

const renderPhysicalItem = ({ item }: tBrandCartItem) => <BrandCartItem item={item} />;

const BrandCart: FC<{
  item: tDetailedCart;
  onRequestToConnect?: (brandUUID: string, brandName: string) => void;
  draftPage?: boolean;
}> = ({ item, onRequestToConnect, draftPage = false }) => {
  const dispatch = useAppDispatch();
  const kNav = useNavigation();
  const isTablet = useIsMobileAndTablet();
  const rCompanyId = useSelector((state) => state.UserReducer.profile.companyRefPath.split("/")?.[1]);
  const rAccountType = useSelector((state) => state.UserReducer.profile.accountType);
  const _physicalItemKeyExtractor = useCallback((myCartProduct, index) => `${myCartProduct.actualSKU}  ${index}`, []);
  const retailerAddressesModal = useRetailerAddressesModal();
  const [sPONumber, setPONumber] = useState("");
  const [sPromotion, setPromotion] = useState("");
  const [promotionError, setPromotionError] = useState("");
  const [connectionStatus, setConnectionStatus] = useState<tConnectionStatus | undefined>(undefined);

  const _editPONumber = () => {
    const formattedPONumber = sPONumber.trim();
    if (formattedPONumber && formattedPONumber !== item.PONumber) {
      dispatch(editPONumber(item.cartId, formattedPONumber));
    }
  };

  const _addPromoCode = async () => {
    const formattedPromotion = sPromotion.trim().toLowerCase();
    if (formattedPromotion && formattedPromotion !== item.discountCode) {
      try {
        await dispatch(addPromoCode(item.cartId, formattedPromotion));
      } catch (error) {
        if (isApiError(error)) {
          const message = error.response?.data?.message ?? "An error occurred please try again.";
          setPromotionError(message);
          dispatch({ type: "RIBBON", message: new Error(message) });
        }
      }
    }
  };

  const handleOnRequestToConnect = useCallback(() => {
    if (!item?.brandUid) throw new Error("Brand UUID not found");
    if (onRequestToConnect) onRequestToConnect(item.brandUid, item.name);
  }, [onRequestToConnect, item?.brandUid, item?.name]);

  const _handleNavToBrand = useCallback(() => {
    kNav.navigate(kRoute.P100, { brand: item.products[0]?.brandName });
  }, [kNav, item.products]);

  const handleOnChangeTextPromotion = (newPromotion: string) => {
    if (promotionError) setPromotionError("");
    setPromotion(newPromotion);
  };

  const handleOnPressShippingAddress = async () => {
    const modal = await retailerAddressesModal.openAsync({ selectedId: item.address.id });
    if (modal.status === "closed") return;
    dispatch(changeCartAddress(item.cartId, modal.data));
  };

  React.useEffect(() => {
    let unsubscribe: (() => void) | undefined;

    if (rAccountType === "retailer") {
      unsubscribe = connectionDb
        .queryBrandRetailerConnection({ brandId: item.brandUid, retailerId: rCompanyId })
        .onSnapshot((snapshot) => {
          setConnectionStatus(snapshot.docs[0]?.data()?.status);
        });
    }

    return () => {
      unsubscribe?.();
    };
  }, [item.brandUid, rAccountType, rCompanyId]);

  return (
    <View style={[styles.brandCartContainer, styles.p4, styles.mb4]}>
      <View
        style={[
          isTablet ? styles.flexColumnReverse : [styles.flexRow, styles.alignStart],
          styles.justifyBetween,
          styles.pb2,
        ]}
      >
        <TouchableOpacity
          style={[styles.flexRow, styles.alignCenter, styles.flex1, isTablet ? styles.mt3 : {}]}
          onPress={_handleNavToBrand}
        >
          <Text style={styles.brandCartTitle}>{item?.name}</Text>
        </TouchableOpacity>
        {!draftPage && (
          <CheckoutButton
            connectionStatus={connectionStatus}
            cart={item}
            onRequestToConnect={handleOnRequestToConnect}
          />
        )}
      </View>
      {!draftPage && (item.draftTitle || item.draftDescription) && (
        <View style={styles.gColumn}>
          <Text style={styles.brandCartTitlePromo}>{item.draftTitle}</Text>
          <Text style={styles.brandCartDetailsPromo}>{item.draftDescription}</Text>
        </View>
      )}
      {item.shippingLeadTime !== undefined && (
        <View style={styles.brandCartShippingContainer}>
          <Text style={styles.brandCartShippingText}>
            Normally ships within {item.shippingLeadTime === "" ? "1-3 Days" : item.shippingLeadTime}
          </Text>
        </View>
      )}
      <FlatList
        keyExtractor={_physicalItemKeyExtractor}
        data={_.cloneDeep(item?.products).sort(sortBySku)}
        renderItem={renderPhysicalItem}
        style={isTablet ? styles.mb3 : styles.mb10}
        maintainVisibleContentPosition={{ minIndexForVisible: 1 }}
        scrollEnabled={false}
      />
      <PdStack direction={isTablet ? "vertical" : "horizontal"} layout={[1, 1]} style={styles.mb2}>
        {rAccountType === "retailer" && (
          <View>
            <Text style={styles.formControlLabel}>PO Number</Text>
            <TextInput
              placeholder={item.PONumber || "Enter PO number"}
              onChangeText={setPONumber}
              onBlur={_editPONumber}
              style={styles.formTextInput}
              placeholderTextColor={kColors.mediumGrey}
            />
          </View>
        )}
        <View>
          <Text style={styles.formControlLabel}>Promotion</Text>
          <View>
            <TextInput
              onChangeText={handleOnChangeTextPromotion}
              onBlur={_addPromoCode}
              value={sPromotion}
              style={[styles.formTextInput, promotionError ? styles.formTextInvalid : {}]}
              placeholderTextColor={kColors.mediumGrey}
              placeholder="Enter promotion code here"
            />
            <Text style={!promotionError ? styles.gHidden : fonts.ErrorSmall}>
              Invalid promotion code or under minimum
            </Text>
          </View>
        </View>
      </PdStack>
      <PdStack direction={isTablet ? "vertical" : "horizontal"} layout={[1, 1]}>
        <View>
          {rAccountType === "retailer" && connectionStatus === "Approved" && (
            <>
              <Text style={styles.formControlLabel}>Ship to:</Text>
              <TouchableOpacity style={styles.shippingAddressButton} onPress={handleOnPressShippingAddress}>
                {!!item.address.name && <Text style={styles.brandCartItemInfoTextPrice}>{item.address.name}</Text>}
                <Text style={styles.brandCartItemInfoTextPrice}>{item.address.address1}</Text>
                {!!item.address.address2 && (
                  <Text style={styles.brandCartItemInfoTextPrice}>{item.address.address2}</Text>
                )}
                <Text style={styles.brandCartItemInfoTextPrice}>{formatShortAddress(item.address)}</Text>
                <View style={styles.shippingAddressRow}>
                  {!!item.address.phone && (
                    <Text style={styles.shippingAddressLabel}>{formatPhoneNumber(item.address.phone)}</Text>
                  )}
                  <Text style={styles.shippingAddressBadge}>Choose a different address</Text>
                </View>
              </TouchableOpacity>
            </>
          )}
        </View>
        <View style={styles.brandCartShippingTotalsContainer}>
          <Text style={styles.brandCartItemInfoTextPrice}>
            Product Total: {formatMoney(item.totalPrice + (item?.discountCost ?? 0))}
          </Text>
          <Text style={draftPage ? styles.gHidden : styles.brandCartItemInfoTextPrice}>
            {item.shippingNumber
              ? `Shipping No.: ${item.shippingNumber}`
              : `Shipping (${formatPercentage(item.shippingPercent ?? 0)}): ${formatMoney(
                  item.totalPriceWithShipping,
                )}`}
          </Text>
          {(item?.discountCost ?? 0) > 0 && (
            <Text style={styles.brandCartItemInfoTextPrice}>Discount: {formatMoney(item.discountCost ?? "0.00")}</Text>
          )}
          <Text style={styles.brandCartItemInfoTextCheckOut}>
            Total: {formatMoney(item.totalPrice + item.totalPriceWithShipping)}
          </Text>
          <ApplyRewardsCheckbox item={item} />
        </View>
      </PdStack>
    </View>
  );
};

export default React.memo(BrandCart);

const styles = StyleSheet.create({
  alignCenter: {
    alignItems: "center",
  },
  alignStart: {
    alignItems: "flex-start",
  },
  bodyText: {
    fontFamily: "Poppins",
    fontSize: 14,
    fontWeight: "300",
    letterSpacing: 1.1,
    lineHeight: 24,
    textAlign: "center",
  },
  brandCartContainer: {
    borderColor: kColors.gray300,
    borderWidth: 1,
    flexDirection: "column",
    flex: 1,
  },
  brandCartDetailsPromo: {
    color: kColors.black,
    fontSize: kStyleConst.kFontSizeMedium,
    fontWeight: "400",
    marginBottom: kStyleConst.kSpacing * 2,
  },
  brandCartItemInfoTextCheckOut: {
    ...kFonts.poppinsBold,
    color: kColors.black,
    fontSize: kStyleConst.kFontSizeMedium,
    fontWeight: "600",
    letterSpacing: 1.1,
    paddingBottom: 2 * kStyleConst.kSpacing,
  },
  brandCartItemInfoTextPrice: {
    ...kFonts.poppinsLight,
    color: kColors.black,
    fontSize: kStyleConst.kFontSizeMedium,
    letterSpacing: 1.1,
    paddingBottom: 2 * kStyleConst.kSpacing,
  },
  brandCartItemName: {
    ...kFonts.poppinsLight,
    color: kColors.brand500,
    letterSpacing: 1.1,
  },
  brandCartShippingContainer: {
    borderColor: kColors.gray,
    flexDirection: "row",
    paddingVertical: 2 * kStyleConst.kSpacing,
  },
  brandCartShippingText: {
    ...kFonts.poppinsLight,
    fontColor: kColors.brand500,
    fontSize: 11,
    marginRight: kStyleConst.kSpacing,
  },
  brandCartShippingTotalsContainer: {
    alignItems: "flex-end",
  },
  brandCartTitle: {
    color: kColors.brand500,
    fontFamily: "Raleway",
    fontSize: 10,
    fontWeight: "700",
    textTransform: "uppercase",
  },
  brandCartTitlePromo: {
    color: kColors.black,
    fontSize: kStyleConst.kFontSizeMedium,
    fontWeight: "600",
    marginVertical: kStyleConst.kSpacing * 2,
  },
  brandInCartItemInfoTextCheckOut: {
    letterSpacing: 1.1,
    textAlign: "right",
  },
  cartQuantityChangeButtonLabel: {
    fontSize: 22,
    fontWeight: "100",
    textAlign: "center",
  },
  flex1: {
    flex: 1,
  },
  flexColumn: {
    flexDirection: "column",
  },
  flexColumnReverse: {
    flexDirection: "column-reverse",
  },
  flexRow: {
    flexDirection: "row",
  },
  flexWrap: {
    flexWrap: "wrap",
  },
  formControlLabel: {
    color: kColors.brand300,
    fontFamily: "Raleway",
    fontSize: 10,
    fontWeight: "700",
    letterSpacing: 1.1,
    marginBottom: kStyleConst.kSpacing,
    textTransform: "uppercase",
  },
  formTextInput: {
    borderColor: kColors.gray300,
    borderWidth: 0.75,
    color: kColors.brand500,
    fontFamily: "Poppins",
    fontSize: 14,
    fontWeight: "300",
    height: 40,
    letterSpacing: 1.1,
    outlineColor: kColors.transparent,
    outlineWidth: 0,
    padding: 2 * kStyleConst.kSpacing,
  },
  formTextInvalid: {
    borderColor: kColors.red,
  },
  gColumn: {
    borderBottomWidth: 2,
    borderColor: kColors.brandPink,
    flex: 1,
    flexDirection: "column",
  },
  gHidden: {
    height: 0,
    outlineColor: kColors.transparent,
    overflow: "hidden",
    width: 0,
  },
  justifyBetween: {
    justifyContent: "space-between",
  },
  justifyCenter: {
    justifyContent: "center",
  },
  mb1: {
    marginBottom: 1 * kStyleConst.kSpacing,
  },
  mb10: {
    marginBottom: 10 * kStyleConst.kSpacing,
  },
  mb2: {
    marginBottom: 2 * kStyleConst.kSpacing,
  },
  mb3: {
    marginBottom: 3 * kStyleConst.kSpacing,
  },
  mb4: {
    marginBottom: 4 * kStyleConst.kSpacing,
  },
  mbAuto: {
    marginBottom: "auto",
  },
  ml2: {
    marginLeft: kStyleConst.kSpacing * 2,
  },
  ml4: {
    marginLeft: 4 * kStyleConst.kSpacing,
  },
  ml5: {
    marginLeft: 5 * kStyleConst.kSpacing,
  },
  mr5: {
    marginRight: 5 * kStyleConst.kSpacing,
  },
  mr7: {
    marginRight: 7 * kStyleConst.kSpacing,
  },
  mt1: {
    marginTop: 1 * kStyleConst.kSpacing,
  },
  mt3: {
    marginTop: kStyleConst.kSpacing * 3,
  },
  my2: {
    marginVertical: 2 * kStyleConst.kSpacing,
  },
  outOfStockLabel: {
    ...kFonts.poppinsRegularMedium,
    color: kColors.red,
    letterSpacing: 1.1,
  },
  p4: {
    padding: 4 * kStyleConst.kSpacing,
  },
  pb2: {
    paddingBottom: 2 * kStyleConst.kSpacing,
  },
  placeholderPortrait: {
    backgroundColor: kColors.white,
    height: 60,
    width: 60,
  },
  removeProductButtonAbsolute: {
    position: "absolute",
    right: 0,
    top: 0,
  },
  removeProductIcon: {
    color: kColors.gray500,
    height: 10,
    marginBottom: "auto",
    marginTop: "auto",
    width: 10,
  },
  shippingAddressBadge: {
    color: kColors.brandDark,
    fontFamily: "Poppins",
    fontSize: 10,
    fontWeight: "500",
    letterSpacing: 0.8,
    marginLeft: "auto",
    textDecorationLine: "underline",
  },
  shippingAddressButton: {
    borderColor: kColors.gray300,
    borderWidth: 0.75,
    paddingHorizontal: 15,
    paddingVertical: 10,
  },
  shippingAddressLabel: {
    ...kFonts.poppinsLight,
    color: kColors.black,
    fontSize: kStyleConst.kFontSizeMedium,
    letterSpacing: 1.1,
  },
  shippingAddressRow: {
    alignItems: "flex-end",
    flexDirection: "row",
    flexWrap: "wrap",
  },
  textGreen: {
    color: kColors.brandDarkGreen,
  },
  textRight: {
    textAlign: "right",
  },
  textSm: {
    fontSize: 10,
  },
  w20: {
    width: 20,
  },
  w250: {
    width: 250,
  },
  w45: {
    width: 45,
  },
  wFull: {
    width: "100%",
  },
});

const webStyles: { [key: string]: CSSProperties } = {
  placeholderPortrait: {
    height: 60,
    width: 60,
    backgroundColor: kColors.white,
  },
} as const;
